import { Component, OnInit } from '@angular/core';
import { PageEvent } from 'src/app/shared/directives/paginator.component';
import { Observable, Subject, catchError, combineLatest, of, switchMap, takeUntil, tap } from 'rxjs';
import { DataService } from 'src/app/shared/services/data.service';
import { PositionService } from '../../../shared/services/position.service';
import { Account } from '../models/account';
import { faPlus, faMinus, faUsers, faChartPie } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-all-accounts',
    templateUrl: './all-accounts.component.html',
    styleUrls: ['./all-accounts.component.scss'],
    standalone: false
})
export class AllAccountsComponent implements OnInit {
    faPlus: any = faPlus;
    faMinus: any = faMinus;
    faUsers: any = faUsers;
    faChartPie: any = faChartPie;

    public displayedColumns: string[] = ['accountId', 'type', 'status', 'bodEquity', 'bodBuyPower', 'curEquity', 'curBuyPower', 'userNamed'];
    public innerDisplayedColumns: string[] = ['symbol', 'quantity', 'avgPrice', 'longShort'];

    public totalRecords: number = 0;
    public allAccounts: any[];
    public pagedAccounts$: Observable<any[]>;

    public totalCurrentAccountNetWorth: number = 0;
    public totalBeginningDayAccountNetWorth: number = 0;
    public pageIndex: number = 0;
    public pageSize: number = 10;

    public loading: boolean = true;
    private onDestroy$ = new Subject<void>();

    constructor(
        private data: DataService,
        private positionService: PositionService
    ) { }

    ngOnInit(): void {
        combineLatest([
            this.data.currentRiaId$.pipe(
                tap(() => this.loading = true), // Set loading to true before making the API call
                switchMap(riaId => this.positionService.getAllAccounts(riaId))
            )
        ])
            .pipe(
                takeUntil(this.onDestroy$), // Unsubscribe when the component is destroyed
                catchError((error) => {
                    console.error("Error fetching accounts:", error);
                    return of([]);
                })
            )
            .subscribe(
                ([accounts]) => {
                    accounts
                        .forEach((element) => {
                            element.beginningDayAccountNetWorth = element.bodEquity;
                            element.beginningDayTradingBuyingPower = element.bodBuyPower;
                            element.currentAccountNetWorth = element.curEquity;
                            element.currentDayTradingBuyingPower = element.curBuyPower;
                            element.userName = element.userNamed;
                        });
                    this.allAccounts = accounts;
                    this.totalRecords = accounts.length;

                    this.totalCurrentAccountNetWorth = accounts
                        .map(d => d.currentAccountNetWorth)
                        .reduce((acc, currentAccountNetWorth) => (acc + currentAccountNetWorth), 0);

                    this.totalBeginningDayAccountNetWorth = accounts
                        .map(d => d.beginningDayAccountNetWorth)
                        .reduce((acc, beginningDayAccountNetWorth) => (acc + beginningDayAccountNetWorth), 0);

                    this.getPagedData();
                }
            );
    }

    public toggleRow(row: any) {
        row.expanded = !row?.expanded;
        if (row.positions === undefined) {
            row.loadingExpanded = true;
            this.positionService.getAccountPositions(row.riaId, row.accountPk).subscribe((positions) => row.positions = positions);
            row.loadingExpanded = false;
        }
    }

    public getPagedData() {
        this.loading = true;
        this.pagedAccounts$ = of(this.allAccounts.slice(this.pageSize * this.pageIndex, this.pageSize * (this.pageIndex + 1)));
        this.loading = false;
    }

    public pageChangeEvent(event: PageEvent) {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.getPagedData();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
